<template>
  <div
    id="signup-form"
    class="signup-form container u-margin-y-8x"
    data-cy="signup-form"
  >
    <div v-if="!showSuccessState">
      <BaseStateIndicator :state="pageState" />
      <div v-if="!pageState">
        <div
          class="
            row
            col-md-12
            u-flex u-margin-bottom-4x
            signup-form__study-info
          "
        >
          <img v-if="studyLogo" alt="study logo" :src="studyLogo" />
          <p
            v-if="studyName"
            :class="[
              isMobile ? 'u-product-h4' : 'u-product-h3',
              { 'u-margin-left-4x': studyLogo }
            ]"
          >
            {{ studyName }}
          </p>
        </div>
        <div class="row">
          <div
            class="col-md-6 u-margin-bottom-6x"
            :style="{ order: isMobile ? '1' : '0' }"
          >
            <BasicForm
              :owner-id="owner"
              :country-id="countryId"
              :countries="countries"
              :isp="isp"
              :product="product"
              @success="showSuccessState = true"
            />
          </div>
          <div class="col-md-6 u-margin-bottom-6x text-center">
            <BrandedSignupSection :owner="owner" />
          </div>
        </div>
      </div>
    </div>
    <SignupConfirmation v-if="showSuccessState" data-cy="signup-confirmation">
      <template #left>
        <div>
          <h2>{{ $t('SignupForm.successMessage.left.title1') }}</h2>

          <p>
            {{ $t('SignupForm.successMessage.left.text1') }}
          </p>

          <p>
            {{ $t('SignupForm.successMessage.left.text2') }}
          </p>

          <h2>{{ $t('SignupForm.successMessage.left.title2') }}</h2>

          <p>
            {{ $t('SignupForm.successMessage.left.text4') }}
          </p>

          <p>{{ $t('SignupForm.successMessage.left.text5') }}</p>

          <i18next path="SignupForm.successMessage.left.text6" tag="p">
            <a :href="faqsLink" target="_blank">{{
              $t('SignupForm.successMessage.left.faqs')
            }}</a
            ><a href="mailto:community@samknows.com" target="_blank">{{
              $t('SignupForm.successMessage.left.email')
            }}</a>
          </i18next>
        </div>
      </template>
      <template #right>
        <h2>{{ $t('SignupForm.successMessage.right.title') }}</h2>

        <p>
          {{ $t('SignupForm.successMessage.right.text') }}
        </p>
      </template>
    </SignupConfirmation>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, Ref, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { BaseStateIndicator } from '@te/cdev-design-system';

import BasicForm from '@/features/volunteer-signup/components/signup/BasicForm.vue';
import SignupConfirmation from '@/features/volunteer-signup/components/SignupConfirmation.vue';
import BrandedSignupSection from '../components/signup/BrandedSignupSection.vue';
import { getMetaData } from '@/features/volunteer-signup/services/signup';
import { ASYNC_STATE } from '@/common/global.interfaces';
import { Country } from '@/features/volunteer-signup/types';
import { formConfig } from '@/features/volunteer-signup/volunteer-form.config';
import { createFaqsLink } from '@/features/volunteer-signup/helpers/volunteer.helper';

export default defineComponent({
  name: 'SignupForm',
  metaInfo() {
    return {
      title: 'Signup'
    };
  },
  components: {
    BasicForm,
    SignupConfirmation,
    BrandedSignupSection,
    BaseStateIndicator
  },
  props: {
    owner: {
      type: String,
      required: true
    },
    country: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const countries: Ref<Country[]> = ref([]);
    const isp = ref(undefined);
    const product = ref(undefined);
    const showSuccessState = ref(false);
    const route = useRoute();
    const router = useRouter();
    const pageState = ref(ASYNC_STATE.LOADING);
    const isMobile = computed((): boolean => {
      return window.innerWidth < 576;
    });

    const formConfigForOwner = ref(formConfig[props.owner] || {});
    const studyLogo = computed(() => {
      return formConfigForOwner.value?.branding?.logo;
    });
    const studyName = computed(() => {
      return formConfigForOwner.value?.studyName;
    });

    const countryId = parseInt(props.country, 10);

    const fixedWireless = ref(undefined);
    function handleFixedWirelessAnswer(input: boolean): void {
      fixedWireless.value = input;
    }

    const faqsLink = computed((): string => {
      return createFaqsLink(props.owner);
    });

    async function getFormMetadata() {
      try {
        const data = await getMetaData();
        pageState.value = ASYNC_STATE.RESOLVED;

        countries.value = data.data.countryData;

        if (!route) {
          return;
        }
        const query = route.query;
        if (query.isp) {
          isp.value = Number(query.isp);
        }
        if (query.product) {
          product.value = Number(query.product);
        }
      } catch (err) {
        pageState.value = ASYNC_STATE.ERROR;
        console.error(err);
      }
    }

    onBeforeMount(async () => {
      const formSettingsForOwner = formConfig[props.owner];
      const isFormConfigMissing = !formSettingsForOwner;

      // Do not show the form for missing configuration
      if (isFormConfigMissing) {
        router.push('/404');
        return;
      }
      await getFormMetadata();
      changeColour();
    });

    function changeColour() {
      document
        .getElementById('signup-form')
        .style.setProperty(
          '--branded-border',
          formConfig[props.owner].branding.colour
        );
    }

    return {
      showSuccessState,
      countryId,
      countries,
      isp,
      product,
      pageState,
      studyLogo,
      studyName,
      isMobile,
      fixedWireless,
      handleFixedWirelessAnswer,
      faqsLink
    };
  }
});
</script>

<style lang="scss">
/**
* CSS #ID used here to access the `style` property through getElementById
* this allows us to assign the branded border its bg colour dynamically
* see changeColour function
*/
#signup-form {
  --branded-border: color(light);
  &::before {
    content: ' ';
    display: block;
    width: 100%;
    height: 0.5rem;
    background-color: var(--branded-border);
    position: absolute;
    top: 0;
    right: 0;
  }
}
.signup-form {
  &__study-info {
    max-height: 4rem;
    flex-wrap: nowrap;
  }
}
</style>
